import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { DocumentCard } from "@/components/DocumentCard";
import { PriceListDialog } from "@/components/PriceListDialog";
import { DEFAULT_DOC_OPTS, computeLinePrice } from "@/lib/pricing";
import { api, apiError } from "@/lib/api";
import { UploadCloud, Loader2, CheckCircle2, Plus, HelpCircle } from "lucide-react";

let counter = 0;

export default function SubmitJob() {
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [pickupAsap, setPickupAsap] = useState(true);
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState("€");
  const [drag, setDrag] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const inputRef = useRef();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      setPricing(data);
      setCurrency(data.currency_symbol || "€");
    });
  }, []);

  const countPages = useCallback(async (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/pagecount", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, pages: data.pages, pagesKnown: data.known, counting: false } : d)));
    } catch {
      setDocs((ds) => ds.map((d) => (d.id === id ? { ...d, pagesKnown: false, counting: false } : d)));
    }
  }, []);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    files.forEach((file) => {
      const id = `d${++counter}`;
      setDocs((ds) => [...ds, { id, file, opts: { ...DEFAULT_DOC_OPTS }, pages: null, pagesKnown: true, counting: true }]);
      countPages(id, file);
    });
  };

  const total = docs.reduce((sum, d) => sum + computeLinePrice(pricing, d.opts, d.pages || 1), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (docs.length === 0) return toast.error("Please add at least one document");
    if (!email) return toast.error("Please enter your email address");
    setSubmitting(true);
    const fd = new FormData();
    docs.forEach((d) => fd.append("files", d.file));
    fd.append("items", JSON.stringify(docs.map((d) => d.opts)));
    fd.append("email", email);
    fd.append("name", name);
    fd.append("pickup_asap", pickupAsap);
    fd.append("pickup_datetime", pickupAsap ? "" : pickupDate);
    fd.append("notes", notes);
    try {
      const { data } = await api.post("/jobs/batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center px-6">
        <div className="max-w-md text-center animate-fade-up" data-testid="submit-success">
          <span className="grid place-items-center w-16 h-16 rounded-full bg-green-50 text-green-600 mx-auto mb-6"><CheckCircle2 size={34} /></span>
          <h1 className="font-heading font-black text-3xl text-ink">Request received</h1>
          <p className="text-slate-600 mt-3">
            {done.jobs.length} document{done.jobs.length > 1 ? "s" : ""} submitted ({done.jobs.map((j) => j.job_number).join(", ")}).
            We've emailed <span className="font-semibold text-ink">{email}</span> a confirmation with a tracking link.
          </p>
          <p className="text-sm text-slate-500 mt-3">Estimated total <span className="font-semibold text-ink">{currency}{Number(done.total).toFixed(2)}</span> — final price confirmed in person at pickup.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <button onClick={() => nav("/login")} data-testid="goto-track" className="bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-lg transition-colors">Track my jobs</button>
            <button onClick={() => { setDone(null); setDocs([]); }} className="border-2 border-slate-300 text-ink font-semibold px-5 py-3 rounded-lg hover:border-ink transition-colors">Submit more</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <PriceListDialog trigger={<button data-testid="view-pricelist-header" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-ink"><HelpCircle size={15} /> Pricing</button>} />
            <button onClick={() => nav("/login")} className="text-sm font-semibold text-slate-700 hover:text-ink">Track my jobs</button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 md:py-14">
        <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">New print request</span>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-3">Upload your documents</h1>
        <p className="text-slate-600 mt-2">Add one or more files and set the options for each — the estimated price updates as you go.</p>

        <form onSubmit={submit} className="mt-8 space-y-6">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            data-testid="file-dropzone"
            className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${drag ? "border-brand bg-orange-50" : "border-slate-300 bg-white hover:border-brand"}`}
          >
            <input ref={inputRef} type="file" multiple className="hidden" data-testid="file-input" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <UploadCloud className="mx-auto text-slate-400" size={36} />
            <p className="font-semibold text-ink mt-3">{docs.length ? "Add more files" : "Drop your files here or click to browse"}</p>
            <p className="text-xs text-slate-500 mt-1">PDF, Word, text files and images — you can upload several</p>
          </div>

          {docs.length > 0 && (
            <div className="space-y-4" data-testid="docs-list">
              {docs.map((d) => (
                <DocumentCard
                  key={d.id} doc={d} pricing={pricing} currency={currency}
                  onChange={(nd) => setDocs((ds) => ds.map((x) => (x.id === nd.id ? nd : x)))}
                  onRemove={() => setDocs((ds) => ds.filter((x) => x.id !== d.id))}
                />
              ))}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div>
              <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Your email *</label>
              <input type="email" required data-testid="submit-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
            <div>
              <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Your name</label>
              <input data-testid="submit-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional"
                className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
            </div>
          </div>

          <div>
            <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Pick-up time</label>
            <div className="grid grid-cols-2 gap-3 max-w-sm">
              <button type="button" data-testid="opt-pickup-asap" onClick={() => setPickupAsap(true)}
                className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${pickupAsap ? "border-brand bg-orange-50 text-ink" : "border-slate-200 bg-white hover:border-slate-300 text-ink"}`}>ASAP</button>
              <button type="button" data-testid="opt-pickup-scheduled" onClick={() => setPickupAsap(false)}
                className={`px-4 py-3 rounded-lg border-2 font-semibold text-sm transition-colors ${!pickupAsap ? "border-brand bg-orange-50 text-ink" : "border-slate-200 bg-white hover:border-slate-300 text-ink"}`}>Schedule</button>
            </div>
            {!pickupAsap && (
              <input type="datetime-local" data-testid="opt-pickup-datetime" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)}
                className="mt-3 w-full max-w-sm px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none text-ink" />
            )}
          </div>

          <div>
            <label className="block uppercase text-xs tracking-[0.18em] font-medium text-slate-500 mb-2">Notes / special requests</label>
            <textarea data-testid="submit-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Anything we should know? e.g. staple top-left, print in landscape, urgent…"
              className="w-full px-4 py-2.5 rounded-lg border-2 border-slate-200 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand resize-y" />
          </div>

          <div className="rounded-xl bg-ink text-white px-5 py-4 flex items-center justify-between" data-testid="order-total">
            <div>
              <span className="uppercase text-xs tracking-[0.18em] text-slate-300 block">Estimated total</span>
              <span className="text-xs text-slate-300">{docs.length} document{docs.length !== 1 ? "s" : ""} · final price confirmed at pickup</span>
            </div>
            <span className="font-heading font-extrabold text-2xl">{currency}{total.toFixed(2)}</span>
          </div>

          <p className="text-xs text-slate-500 flex items-center gap-2">
            <HelpCircle size={13} /> This is an estimate based on the files uploaded. The final price will be confirmed in person when you pick up your printing.{" "}
            <PriceListDialog trigger={<button type="button" className="text-brand font-semibold underline">See how pricing works</button>} />
          </p>

          <button type="submit" disabled={submitting} data-testid="submit-job-button"
            className="w-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="animate-spin" size={18} /> Submitting…</> : <>Submit print request{docs.length ? ` — ${currency}${total.toFixed(2)}` : ""}</>}
          </button>
        </form>
      </main>
    </div>
  );
}
